import React, { useLayoutEffect, useRef, useState } from 'react';
import { Button, ButtonProps } from '../ui/button';
import { cn } from '@/lib/utils';

type Props = ButtonProps & { loading?: boolean; size?: number; timeToActivate?: number };
export interface ISVGProps extends React.SVGProps<SVGSVGElement> {
  size?: number;
  className?: string;
}

/**
 *  A styled button component.
 * @param props - The props for the component
 * @param props.variant - The variant for the button. Can be "ghost", "outline", "destructive", "secondary" or "link"
 * @returns The Button component
 * @example <StyledButton variant="ghost">Hello World</StyledButton>
 */
const StyledButton = React.forwardRef<HTMLButtonElement, Props>((props, ref) => {
  const { size = 24, children, loading, className, timeToActivate, ...rest } = props;
  const animationOptions = 'transition-all duration-150';
  const generalClasses = 'w-[fit-content] rounded-full px-6 text-sm text-neutralDark text-sm dark:bg-muted/40';
  const ghostClass = 'hover:bg-accent/10';
  const outlineClass = 'border-[1px] border-accentColor/20 bg-transparent hover:border-accentColor/30';
  const linkClass = 'text-secondaryColor';
  const defaultClass = 'bg-accent/5 hover:bg-accent/15';

  const [timer, setTimer] = useState(timeToActivate || 0);
  const [intervalRef, setIntervalRef] = useState<NodeJS.Timeout>();
  const percentDone = timeToActivate ? Math.min(100, 100 - (timer / timeToActivate) * 100) : 100;
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [bgColor, setBgColor] = useState('');

  const fillBackgroundStyle = timeToActivate
    ? timer > 0 && timer < timeToActivate
      ? {
          background: `linear-gradient(to right, ${bgColor} ${percentDone}%, transparent ${percentDone}%)`,
          opacity: 0.75,
        }
      : timer === timeToActivate
      ? { background: bgColor, opacity: 0.75 }
      : {
          background: `linear-gradient(to right, ${bgColor} ${percentDone}%, transparent ${percentDone}%)`,
          opacity: 1,
        }
    : {};

  const style = (() => {
    switch (props.variant) {
      case 'link':
        return cn(generalClasses, linkClass);
      case 'outline':
        return cn(generalClasses, outlineClass);
      case 'ghost':
        return cn(generalClasses, ghostClass);
      case 'destructive':
        return cn(generalClasses, 'bg-destructive/70');
      default:
        return cn(generalClasses, defaultClass);
    }
  })();

  const startTimer = () => {
    // count the timer ever 10 ms
    const interval = setInterval(() => {
      setTimer((prev) => {
        const timeLeft = prev - 10;
        if (timeLeft <= 0) {
          clearInterval(interval);
          return 0;
        }
        return timeLeft;
      });
    }, 10);
    setIntervalRef(interval);
  };

  useLayoutEffect(() => {
    if (buttonRef.current && timer > 0) {
      const color = getComputedStyle(buttonRef.current).backgroundColor;
      setBgColor(color);
    }

    //eslint-disable-next-line
  }, []);

  return (
    <Button
      ref={(el) => {
        // @ts-expect-error: Ref might be readonly, but we safely assign it
        buttonRef.current = el;
        if (typeof ref === 'function') ref(el);
        else if (ref) (ref as React.MutableRefObject<HTMLButtonElement>).current = el!;
      }}
      size="sm"
      variant={props.variant || 'ghost'}
      style={fillBackgroundStyle}
      className={cn(style, animationOptions, className)}
      {...rest}
      // if timeToActivate is set, the button will be disabled for the given time
      {...(timeToActivate
        ? {
            onMouseEnter: startTimer,
            onMouseLeave: () => {
              clearInterval(intervalRef);
              setTimer(timeToActivate);
            },
            ...(timer > 0
              ? {
                  onClick: (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  },
                  onTouchStart: (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  },
                }
              : {}),
          }
        : {})}>
      {loading ? <LoadingSpinner size={size} /> : children}
    </Button>
  );
});

StyledButton.displayName = 'StyledButton';

export default StyledButton;

export const LoadingSpinner = ({ size = 24, className, ...props }: ISVGProps) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      {...props}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn('animate-spin', className)}>
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
};
